export const MAX_IMAGE_BYTES = 20 * 1024 * 1024
export const IMAGE_DOWNLOAD_TIMEOUT_MS = 15000

export type ImageFormat = {
  mime: string
  wordType?: 'png' | 'jpg' | 'gif' | 'bmp'
}

// 只按图片签名判断格式，不依赖 URL 后缀或图床的 Content-Type。
export function detectImageFormat(bytes: Uint8Array): ImageFormat | null {
  const startsWith = (signature: number[]) =>
    signature.every((value, index) => bytes[index] === value)
  const ascii = (start: number, length: number) =>
    String.fromCharCode(...Array.from(bytes.subarray(start, start + length)))

  if (
    bytes.length >= 24 &&
    startsWith([137, 80, 78, 71, 13, 10, 26, 10]) &&
    ascii(12, 4) === 'IHDR'
  ) {
    return { mime: 'image/png', wordType: 'png' }
  }
  if (bytes.length >= 4 && startsWith([255, 216, 255])) {
    return { mime: 'image/jpeg', wordType: 'jpg' }
  }
  if (bytes.length >= 13 && ['GIF87a', 'GIF89a'].includes(ascii(0, 6))) {
    return { mime: 'image/gif', wordType: 'gif' }
  }
  if (bytes.length >= 26 && ascii(0, 2) === 'BM') {
    return { mime: 'image/bmp', wordType: 'bmp' }
  }
  if (bytes.length >= 16 && ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') {
    return { mime: 'image/webp' }
  }
  if (
    bytes.length >= 22 &&
    startsWith([0, 0, 1, 0]) &&
    (bytes[4] || bytes[5])
  ) {
    return { mime: 'image/x-icon' }
  }
  if (bytes.length >= 16 && ascii(4, 4) === 'ftyp') {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
    const boxSize = view.getUint32(0)
    if (boxSize >= 16 && boxSize <= bytes.length) {
      for (let offset = 8; offset + 4 <= Math.min(boxSize, 256); offset += 4) {
        if (offset === 12) continue
        if (['avif', 'avis'].includes(ascii(offset, 4)))
          return { mime: 'image/avif' }
      }
    }
  }
  // SVG 只作为图片解码并栅格化，不插入页面 DOM；拒绝带 DTD 的文档。
  let prefix = new TextDecoder().decode(bytes.subarray(0, 4096)).trimStart()
  if (prefix.startsWith('<?xml')) {
    const end = prefix.indexOf('?>')
    if (end < 0) return null
    prefix = prefix.slice(end + 2).trimStart()
  }
  while (prefix.startsWith('<!--')) {
    const end = prefix.indexOf('-->')
    if (end < 0) return null
    prefix = prefix.slice(end + 3).trimStart()
  }
  if (/^<svg[\s>]/.test(prefix)) return { mime: 'image/svg+xml' }
  return null
}
